// src/components/QuantumGraph.tsx

/**
 * Quantum analytics visualization component for the Quantik Mind enterprise frontend.
 *
 * This module is responsible for:
 * - loading analytics data for the current run and project
 * - rendering efficiency trend analytics
 * - rendering service risk heatmaps
 * - comparing Quantum selection against alternative strategies
 * - estimating carbon impact for functional runs
 *
 * The implementation intentionally preserves the current analytics logic and
 * troubleshooting-oriented debug output while improving readability,
 * structure, and inline documentation.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
  ReferenceArea,
} from 'recharts';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import { getCarbonCumulative } from '../api/analyticsApi';
import Button from './common/ActionButton';
import { useRunAnalytics } from '../features/analytics/hooks/useRunAnalytics';
import { formatPercentage } from '../utils/percentage';
import {
  calculateAnnualCarbonProjectionKg,
  calculateCarbonImpact,
  calculateObservedRunsPerDay,
  CARBON_CONSTANTS,
  CHART_COLORS,
  formatDate,
  kgToTonnes,
  type ComparatorBar,
  type HeatmapCell,
  type TrendPoint,
} from '../features/analytics/utils/analyticsTransforms';
import type { Run } from '../types';

/* ------------------------------------------------------------------ */
/*  Theme and colors                                                   */
/* ------------------------------------------------------------------ */

/**
 * Centralized color palette used by the analytics charts.
 */
const COLORS = CHART_COLORS;

/* ------------------------------------------------------------------ */
/*  Carbon calculations and constants                                  */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Chart 1: Efficiency trend                                          */
/* ------------------------------------------------------------------ */

/**
 * Render the efficiency trend chart with adaptive aggregation and zoom.
 *
 * @param props - Chart props.
 * @returns Efficiency trend chart.
 */
function EfficiencyTrendChart({
  data,
  currentRun,
}: {
  data: TrendPoint[];
  currentRun: any;
}) {
  const [zoomState, setZoomState] = useState<{ left: number; right: number } | null>(
    null
  );
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return (
      <Alert severity="info">
        No efficiency trend data available yet. Run more tests to see trends.
      </Alert>
    );
  }

  /**
   * Apply zoom filtering and adaptive aggregation based on time span and point
   * density.
   */
  const displayData = useMemo(() => {
    let filtered = data;

    if (zoomState) {
      const left = Math.min(zoomState.left, zoomState.right);
      const right = Math.max(zoomState.left, zoomState.right);
      filtered = data.filter(
        (point: any) => Number(point.ts) >= left && Number(point.ts) <= right
      );
    }

    if (filtered.length === 0) return filtered;

    const minTs = Math.min(...filtered.map((point) => point.ts));
    const maxTs = Math.max(...filtered.map((point) => point.ts));
    const dayRange = (maxTs - minTs) / (1000 * 60 * 60 * 24);
    const pointCount = filtered.length;

    let intervalMs: number | null = null;

    if (pointCount <= 50) {
      return filtered;
    }

    if (dayRange < 2) {
      return filtered;
    }

    if (dayRange > 21 && pointCount > 100) {
      intervalMs = 1000 * 60 * 60 * 24;
    } else if (dayRange > 7 && pointCount > 80) {
      intervalMs = 1000 * 60 * 60 * 6;
    } else if (dayRange > 3 && pointCount > 60) {
      intervalMs = 1000 * 60 * 60;
    } else {
      return filtered;
    }

    const buckets: Record<number, TrendPoint[]> = {};

    filtered.forEach((point) => {
      const bucketKey = Math.floor(point.ts / intervalMs!) * intervalMs!;
      if (!buckets[bucketKey]) {
        buckets[bucketKey] = [];
      }
      buckets[bucketKey].push(point);
    });

    const avg = (values: number[]) =>
      values.reduce((acc, value) => acc + value, 0) / values.length;

    return Object.entries(buckets)
      .map(([bucketTs, points]) => {
        const ts = Number(bucketTs);

        return {
          date: new Date(ts).toISOString(),
          ts,
          dateLabel: formatDate(ts),
          risk_coverage: avg(points.map((point) => point.risk_coverage)),
          tests_executed_pct: avg(
            points.map((point) => point.tests_executed_pct)
          ),
          efficiency_score: avg(points.map((point) => point.efficiency_score)),
          aggregatedCount: points.length,
        };
      })
      .sort((a, b) => a.ts - b.ts);
  }, [data, zoomState]);

  /**
   * Derive summary information for the current zoom and aggregation level.
   */
  const { dayRange, aggregationLevel, totalPoints } = useMemo(() => {
    if (displayData.length === 0) {
      return { dayRange: 0, aggregationLevel: 'none', totalPoints: 0 };
    }

    const minTs = Math.min(...displayData.map((point) => point.ts));
    const maxTs = Math.max(...displayData.map((point) => point.ts));
    const days = Math.ceil((maxTs - minTs) / (1000 * 60 * 60 * 24));
    const points = displayData.reduce(
      (sum, point) => sum + (point.aggregatedCount || 1),
      0
    );

    let level = 'all runs';
    if (displayData.some((point) => (point.aggregatedCount || 1) > 1)) {
      if (days > 21) level = 'daily avg';
      else if (days > 7) level = '6h avg';
      else if (days > 1) level = 'hourly avg';
    }

    return { dayRange: days, aggregationLevel: level, totalPoints: points };
  }, [displayData]);
  const hasAveragedPoints = displayData.some(
    (point) => (point.aggregatedCount || 1) > 1
  );
  const trendTitle =
    aggregationLevel === 'daily avg'
      ? 'Historical trend — daily average'
      : hasAveragedPoints
      ? `Historical trend — ${aggregationLevel}`
      : 'Historical trend — individual runs';

  /**
   * Resolve a descriptive label for the current zoom level.
   */
  const getZoomLevel = () => {
    if (dayRange <= 1) return `🔍 Single Day View (${totalPoints} runs)`;
    if (dayRange <= 7) {
      return `📅 Weekly View (${totalPoints} runs, ${aggregationLevel})`;
    }
    if (dayRange <= 14) {
      return `📅 Bi-Weekly View (${totalPoints} runs, ${aggregationLevel})`;
    }
    return `📊 Monthly View (${totalPoints} runs, ${aggregationLevel})`;
  };

  /**
   * Reset the current zoom state.
   */
  const zoomOut = () => {
    setZoomState(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  /**
   * Safely convert chart labels into numeric timestamps.
   *
   * @param value - Raw Recharts label value.
   * @returns Numeric timestamp or null.
   */
  const toNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
  };

  /**
   * Apply a zoom area from the current reference selection.
   */
  const zoom = () => {
    if (
      refAreaLeft == null ||
      refAreaRight == null ||
      refAreaLeft === refAreaRight
    ) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }

    const left = Math.min(refAreaLeft, refAreaRight);
    const right = Math.max(refAreaLeft, refAreaRight);

    setZoomState({ left, right });
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  /**
   * Custom tooltip used by the trend chart.
   */
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const item = payload[0].payload;
    const aggregatedCount = item.aggregatedCount || 1;

    return (
      <Box
        sx={{
          bgcolor: 'rgba(11, 18, 32, 0.95)',
          border: `1px solid ${COLORS.grid}`,
          borderRadius: 1,
          p: 1.5,
          minWidth: 200,
        }}
      >
        <Typography fontSize={12} fontWeight={700} color="white" mb={0.5}>
          {item.dateLabel ?? formatDate(item.ts)}
        </Typography>

        {aggregatedCount > 1 && (
          <Typography
            fontSize={10}
            color={COLORS.quantum}
            mb={0.5}
            fontWeight={700}
            fontStyle="italic"
          >
            📊 {aggregatedCount} runs averaged
          </Typography>
        )}

        <Typography fontSize={11} color={COLORS.coverage}>
          Selected Legacy Score Mass: {formatPercentage(Number(item.risk_coverage))}
        </Typography>
        <Typography fontSize={11} color={COLORS.quantum}>
          Tests Executed: {formatPercentage(Number(item.tests_executed_pct))}
        </Typography>
        <Typography fontSize={11} color={COLORS.efficiency}>
          Efficiency Score: {Number(item.efficiency_score).toFixed(1)}
        </Typography>
      </Box>
    );
  };

  const isCompleted = currentRun.status === 'completed';

  const currentRiskCoverage = isCompleted
    ? currentRun.risk_coverage ?? null
    : null;

  const currentTotalTests = isCompleted
    ? (currentRun as any).total_experiments ?? (currentRun as any).total_tests ?? 0
    : 0;

  const currentSelectedTests = isCompleted
    ? (currentRun as any).selected_experiments ?? (currentRun as any).selected_tests ?? 0
    : 0;

  const currentTestsPct =
    currentTotalTests > 0
      ? (currentSelectedTests / currentTotalTests) * 100
      : null;

  const currentRiskDensity =
    currentRiskCoverage != null && currentTestsPct != null && currentTestsPct > 0
      ? currentRiskCoverage / currentTestsPct
      : null;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontSize={13} fontWeight={600} color={COLORS.axis}>
            {trendTitle}
          </Typography>

          <Tooltip
            title={
              <Box>
                <Typography fontSize={11} fontWeight={700} mb={0.5}>
                  Legacy Score Concentration = (selected p_initial mass ÷ tests executed) × 25
                </Typography>
                <Typography fontSize={10}>
                  Measures how much risk you cover per test executed.
                </Typography>
                <Typography
                  fontSize={10}
                  mt={1}
                  fontWeight={700}
                  color={COLORS.quantum}
                >
                  🎯 Smart Aggregation:
                </Typography>
                <Typography fontSize={10}>
                  • 30+ runs → auto-aggregated by time
                </Typography>
                <Typography fontSize={10}>
                  • Monthly view: daily average
                </Typography>
                <Typography fontSize={10}>
                  • Weekly view: 6-hour average
                </Typography>
                <Typography fontSize={10}>
                  • Daily view: hourly average
                </Typography>
                <Typography fontSize={10} mt={0.5} fontStyle="italic">
                  Zoom in to see more granular data!
                </Typography>
              </Box>
            }
            arrow
            placement="right"
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 16, color: COLORS.axis, cursor: 'help' }}
            />
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Typography fontSize={12} fontWeight={600} color={COLORS.quantum}>
            {getZoomLevel()}
          </Typography>

          {zoomState && (
            <Button
              size="small"
              startIcon={<ZoomOutIcon />}
              onClick={zoomOut}
              sx={{
                color: COLORS.quantum,
                borderColor: COLORS.quantum,
                fontWeight: 700,
                fontSize: 11,
                '&:hover': {
                  bgcolor: 'rgba(57, 255, 20, 0.1)',
                  borderColor: COLORS.quantum,
                },
              }}
              variant="outlined"
            >
              Reset Zoom
            </Button>
          )}
        </Box>
      </Box>

      {!zoomState && totalPoints > 30 && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: 'rgba(57, 255, 20, 0.1)',
            border: `1px solid ${COLORS.quantum}`,
            '& .MuiAlert-icon': { color: COLORS.quantum },
          }}
        >
          <Typography fontSize={12} color="white">
            🎯 <strong>Smart aggregation active:</strong> Showing {aggregationLevel}{' '}
            of {totalPoints} runs. <strong>Zoom in</strong> to see more detail!
          </Typography>
        </Alert>
      )}

      {!zoomState && totalPoints <= 30 && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: 'rgba(47, 128, 237, 0.1)',
            border: `1px solid ${COLORS.coverage}`,
            '& .MuiAlert-icon': { color: COLORS.coverage },
          }}
        >
          <Typography fontSize={12} color="white">
            🖱️ <strong>Click and drag</strong> on the chart to zoom.{' '}
            <strong>Double-click</strong> to reset.
          </Typography>
        </Alert>
      )}

      <Box onDoubleClick={zoomOut} sx={{ cursor: zoomState ? 'zoom-out' : 'crosshair' }}>
        <ResponsiveContainer width="100%" height={450}>
          <LineChart
            data={displayData}
            margin={{ top: 20, right: 80, bottom: 20, left: 20 }}
            onMouseDown={(event: any) => {
              const x = toNumber(event?.activeLabel);
              if (x != null) setRefAreaLeft(x);
            }}
            onMouseMove={(event: any) => {
              if (refAreaLeft == null) return;
              const x = toNumber(event?.activeLabel);
              if (x != null) setRefAreaRight(x);
            }}
            onMouseUp={zoom}
          >
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="4 4" />

            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              stroke={COLORS.axis}
              style={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Percentage (%)',
                angle: -90,
                position: 'insideLeft',
                style: { fill: COLORS.axis, fontWeight: 600 },
              }}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 'auto']}
              stroke={COLORS.efficiency}
              style={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Efficiency Score',
                angle: 90,
                position: 'insideRight',
                style: { fill: COLORS.efficiency, fontWeight: 600 },
              }}
            />

            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => formatDate(value)}
              stroke={COLORS.axis}
              style={{ fontSize: 11, fontWeight: 600 }}
            />

            <RechartsTooltip content={<CustomTooltip />} />

            <Legend
              wrapperStyle={{ fontSize: 13, fontWeight: 600 }}
              iconType="line"
            />

            <Line
              yAxisId="left"
              type="monotone"
              dataKey="risk_coverage"
              name="Selected Legacy Score Mass %"
              stroke={COLORS.coverage}
              strokeWidth={2}
              dot={{ r: 4, fill: COLORS.coverage, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: COLORS.coverage }}
            />

            <Line
              yAxisId="left"
              type="monotone"
              dataKey="tests_executed_pct"
              name="Tests Executed %"
              stroke={COLORS.quantum}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4, fill: COLORS.quantum, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: COLORS.quantum }}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="efficiency_score"
              name="Efficiency Score"
              stroke={COLORS.efficiency}
              strokeWidth={2}
              dot={{ r: 4, fill: COLORS.efficiency, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: COLORS.efficiency }}
            />

            {refAreaLeft != null &&
            refAreaRight != null &&
            refAreaLeft !== refAreaRight ? (
              <ReferenceArea
                yAxisId="left"
                x1={Math.min(refAreaLeft, refAreaRight)}
                x2={Math.max(refAreaLeft, refAreaRight)}
                strokeOpacity={0.35}
                fill={COLORS.quantum}
                fillOpacity={0.25}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: 'rgba(57, 255, 20, 0.1)',
          border: `2px solid ${COLORS.quantum}`,
          borderRadius: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: COLORS.quantum,
              borderRadius: '50%',
            }}
          />
          <Typography fontWeight={800} fontSize={14} color="white">
            NOW
          </Typography>
        </Box>

        {isCompleted && currentRiskCoverage != null && currentTestsPct != null && currentRiskDensity != null ? (
          <Typography fontSize={13} mt={1} color={COLORS.axis}>
            Current run: {currentSelectedTests} / {currentTotalTests} selected,{' '}
            {formatPercentage(currentTestsPct)} tests executed,{' '}
            {formatPercentage(currentRiskCoverage)} observed risk coverage,{' '}
            {currentRiskDensity.toFixed(2)}x risk density
          </Typography>
        ) : isCompleted ? (
          <Typography fontSize={13} mt={1} color="warning.main">
            Current run KPIs unavailable because completed-run analytics fields
            are missing.
          </Typography>
        ) : (
          <Typography fontSize={13} mt={1} color="warning.main">
            Current run in progress – KPIs available when completed
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Chart 2: Service risk heatmap                                     */
/* ------------------------------------------------------------------ */

/**
 * Render the service risk heatmap across recent runs.
 *
 * @param props - Heatmap props.
 * @returns Service risk heatmap.
 */
function ServiceRiskHeatmap({
  runs,
  data,
  currentRunId,
}: {
  runs: Array<{ run_id: string; run_number: number; created_at: string }>;
  data: HeatmapCell[];
  currentRunId?: string;
}) {
  if (!data || data.length === 0) {
    return (
      <Alert severity="info">
        No service risk data available yet. Need multiple runs to show heatmap.
      </Alert>
    );
  }

  const currentRunAvailable = Boolean(
    currentRunId && runs.some((run) => run.run_id === currentRunId)
  );
  const effectiveCurrentRunId = currentRunAvailable ? currentRunId : undefined;

  const orderedRuns = [...runs].sort((a, b) => {
    if (effectiveCurrentRunId && a.run_id === effectiveCurrentRunId) return 1;
    if (effectiveCurrentRunId && b.run_id === effectiveCurrentRunId) return -1;
    return (
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  });

  const cellsByRun = orderedRuns.map((run) => {
    const runCells = data
      .filter((cell) => cell.run_id === run.run_id)
      .sort((a, b) => a.rank - b.rank);

    return { run, cells: runCells };
  });

  return (
    <Box>
      <Typography fontSize={14} color={COLORS.axis} mb={2} fontWeight={600}>
        Last {runs.length} runs × Top 8 services per run
      </Typography>

      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(36px, 60px) repeat(${runs.length}, minmax(0, 1fr))`,
            gap: '8px',
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box />

          {cellsByRun.map(({ run }) => {
            const isNow = Boolean(currentRunId && run.run_id === currentRunId);

            return (
              <Box key={run.run_id} sx={{ textAlign: 'center', pb: 1 }}>
                <Typography fontSize={12} fontWeight={700} color="white">
                  {isNow ? '👑 NOW' : `Run ${run.run_number}`}
                </Typography>
                <Typography fontSize={10} color={COLORS.axis} mt={0.5}>
                  {formatDate(run.created_at)}
                </Typography>
              </Box>
            );
          })}

          {[1, 2, 3, 4, 5, 6, 7, 8].map((rank) => (
            <React.Fragment key={rank}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(17, 24, 39, 0.5)',
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: 12,
                  color: COLORS.axis,
                  minHeight: 60,
                }}
              >
                #{rank}
              </Box>

              {cellsByRun.map(({ run, cells }) => {
                const cell = cells.find((item) => item.rank === rank);

                return (
                  <Tooltip
                    key={`${run.run_id}-${rank}`}
                    title={
                      cell ? (
                        <Box>
                          <Typography fontSize={12} fontWeight={700}>
                            {cell.service}
                          </Typography>
                          <Typography fontSize={11}>
                            Risk: {formatPercentage(cell.risk_percentage)}
                          </Typography>
                          <Typography fontSize={11}>
                            Level: {cell.risk_level}
                          </Typography>
                        </Box>
                      ) : (
                        ''
                      )
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        minHeight: 60,
                        bgcolor: cell
                          ? cell.color
                          : 'rgba(255, 255, 255, 0.03)',
                        border: cell
                          ? 'none'
                          : '1px dashed rgba(255, 255, 255, 0.1)',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 1,
                        cursor: cell ? 'pointer' : 'default',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: cell ? 'scale(1.05)' : 'none',
                        },
                      }}
                    >
                      {cell && (
                        <>
                          <Typography
                            fontWeight={700}
                            fontSize={9}
                            color="white"
                            sx={{
                              opacity: 0.9,
                              textAlign: 'center',
                              px: 0.5,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '100%',
                            }}
                          >
                            {cell.service}
                          </Typography>
                          <Typography
                            fontWeight={800}
                            fontSize={15}
                            color="white"
                            mt={0.5}
                          >
                            {formatPercentage(cell.risk_percentage)}
                          </Typography>
                        </>
                      )}

                      {cell && currentRunId && cell.run_id === currentRunId && (
                        <Typography
                          fontSize={9}
                          fontWeight={800}
                          color={COLORS.quantum}
                          mt={0.3}
                        >
                          NOW
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Box display="flex" gap={4} mt={3} justifyContent="center" flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              bgcolor: COLORS.heatmap.low,
              borderRadius: 1,
            }}
          />
          <Typography fontSize={12} fontWeight={600} color="white">
            Low Risk (Bottom 33%)
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              bgcolor: COLORS.heatmap.medium,
              borderRadius: 1,
            }}
          />
          <Typography fontSize={12} fontWeight={600} color="white">
            Medium Risk (Middle 33%)
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              bgcolor: COLORS.heatmap.high,
              borderRadius: 1,
            }}
          />
          <Typography fontSize={12} fontWeight={600} color="white">
            High Risk (Top 33%)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Chart 3: What-if comparator                                       */
/* ------------------------------------------------------------------ */

/**
 * Render the strategy comparison charts.
 *
 * @param props - Comparator props.
 * @returns What-if comparator.
 */
function WhatIfComparator({
  data,
  efficiencyMultiplier,
  runType,
  error,
}: {
  data: ComparatorBar[];
  efficiencyMultiplier: number;
  runType?: string;
  error?: string;
}) {
  if (!data || data.length < 3) {
    return (
      <Alert severity="info">
        {error ||
          'What-if comparison is unavailable until measured project baseline data exists. No illustrative Random, Full Suite, or competitor values are shown.'}
      </Alert>
    );
  }

  const CustomLabel = ({ x, y, width, value, type }: any) => {
    return (
      <text
        x={x + width + 10}
        y={y + 20}
        fill="white"
        fontSize={14}
        fontWeight={700}
      >
        {type === 'tests' && formatPercentage(value)}
        {type === 'coverage' && formatPercentage(value)}
        {type === 'time' && `${value.toFixed(1)} min`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, type }: any) => {
    if (!active || !payload || !payload[0]) return null;

    const item = payload[0].payload;

    return (
      <Box
        sx={{
          bgcolor: 'rgba(11, 18, 32, 0.95)',
          border: `1px solid ${COLORS.grid}`,
          borderRadius: 1,
          p: 1.5,
          minWidth: 180,
        }}
      >
        <Typography fontSize={13} fontWeight={700} color="white" mb={0.5}>
          {item.strategy}
        </Typography>
        <Typography fontSize={11} color={COLORS.axis} mb={0.5}>
          {item.sourceLabel}
        </Typography>

        {type === 'tests' && (
          <>
            <Typography fontSize={12} color={COLORS.quantum}>
              Tests: {formatPercentage(item.testsPct)}
            </Typography>
            <Typography fontSize={12} color={COLORS.axis}>
              Percentage of total tests run
            </Typography>
          </>
        )}

        {type === 'coverage' && (
          <>
            <Typography fontSize={12} color={COLORS.coverage}>
              Selected Legacy Score Mass: {formatPercentage(item.coveragePct)}
            </Typography>
            <Typography fontSize={12} color={COLORS.axis}>
              Total risk captured
            </Typography>
          </>
        )}

        {type === 'time' && (
          <>
            <Typography fontSize={12} color={COLORS.efficiency}>
              Time: {item.timeMinutes.toFixed(1)} minutes
            </Typography>
            <Typography fontSize={12} color={COLORS.axis}>
              Estimated execution time
            </Typography>
          </>
        )}
      </Box>
    );
  };
  const quantumSourceLabel = data[0]?.sourceLabel || 'Project average, last 30 days';
  const quantikSourceSentence =
    quantumSourceLabel === 'Current run'
      ? 'Quantik Mind values use the selected current run. Random 50% and Full Suite are illustrative reference estimates from the analytics API.'
      : `Quantik Mind values use ${quantumSourceLabel}. Random 50% and Full Suite are illustrative reference estimates from the analytics API.`;

  return (
    <Box>
      <Box mb={5}>
        <Typography variant="h6" fontWeight={800} mb={2} color="white">
          Tests Executed
        </Typography>
        <Typography fontSize={12} color={COLORS.axis} mb={2}>
          {quantikSourceSentence}
        </Typography>

        <ResponsiveContainer width="100%" height={160}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 100, bottom: 5, left: 160 }}
          >
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="4 4" />
            <XAxis
              type="number"
              domain={[0, 100]}
              stroke={COLORS.axis}
              style={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Percentage of total tests (%)',
                position: 'insideBottom',
                offset: -5,
                style: {
                  fill: COLORS.axis,
                  fontWeight: 600,
                  fontSize: 12,
                },
              }}
            />
            <YAxis
              type="category"
              dataKey="strategy"
              stroke={COLORS.axis}
              style={{ fontSize: 13, fontWeight: 600 }}
              width={150}
            />
            <RechartsTooltip content={<CustomTooltip type="tests" />} />
            <Bar dataKey="testsPct" label={<CustomLabel type="tests" />}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box mb={5}>
        <Typography variant="h6" fontWeight={800} mb={2} color="white">
          Selected Legacy Score Mass
        </Typography>
        <Typography fontSize={12} color={COLORS.axis} mb={2}>
          Baseline coverage values are illustrative estimates, not measured
          competitor results.
        </Typography>

        <ResponsiveContainer width="100%" height={160}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 100, bottom: 5, left: 160 }}
          >
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="4 4" />
            <XAxis
              type="number"
              domain={[0, 100]}
              stroke={COLORS.axis}
              style={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Percentage of total risk covered (%)',
                position: 'insideBottom',
                offset: -5,
                style: {
                  fill: COLORS.axis,
                  fontWeight: 600,
                  fontSize: 12,
                },
              }}
            />
            <YAxis
              type="category"
              dataKey="strategy"
              stroke={COLORS.axis}
              style={{ fontSize: 13, fontWeight: 600 }}
              width={150}
            />
            <RechartsTooltip content={<CustomTooltip type="coverage" />} />
            <Bar dataKey="coveragePct" label={<CustomLabel type="coverage" />}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {runType === 'functional' && data[0].timeMinutes > 0 && (
        <Box mb={5}>
          <Typography variant="h6" fontWeight={800} mb={2} color="white">
            Execution Time
          </Typography>
          <Typography fontSize={12} color={COLORS.axis} mb={2}>
            Execution times are estimated from project test-duration baselines.
          </Typography>

          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 100, bottom: 5, left: 160 }}
            >
              <CartesianGrid stroke={COLORS.grid} strokeDasharray="4 4" />
              <XAxis
                type="number"
                stroke={COLORS.axis}
                style={{ fontSize: 12, fontWeight: 600 }}
                label={{
                  value: 'Time (minutes)',
                  position: 'insideBottom',
                  offset: -5,
                  style: {
                    fill: COLORS.axis,
                    fontWeight: 600,
                    fontSize: 12,
                  },
                }}
              />
              <YAxis
                type="category"
                dataKey="strategy"
                stroke={COLORS.axis}
                style={{ fontSize: 13, fontWeight: 600 }}
                width={150}
              />
              <RechartsTooltip content={<CustomTooltip type="time" />} />
              <Bar dataKey="timeMinutes" label={<CustomLabel type="time" />}>
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Alert
        severity="info"
        sx={{
          mt: 4,
          bgcolor: 'rgba(16, 185, 129, 0.1)',
          border: `2px solid ${COLORS.heatmap.low}`,
          '& .MuiAlert-icon': {
            color: COLORS.coverage,
          },
        }}
      >
        <Typography fontWeight={800} fontSize={14} color="white">
          Estimated Comparison Ratio: {efficiencyMultiplier.toFixed(2)}
        </Typography>
        <Typography fontSize={13} mt={0.5} color={COLORS.axis}>
          {runType === 'functional' && data[0].timeMinutes > 0 ? (
            <>
              Quantik Mind ({quantumSourceLabel}) captured {formatPercentage(data[0].coveragePct)} observed
              risk coverage with {formatPercentage(data[0].testsPct)} of tests. The{' '}
              {(data[2].timeMinutes - data[0].timeMinutes).toFixed(1)} minute
              time difference is an illustrative estimate versus the full-suite
              reference.
            </>
          ) : (
            <>
              Quantik Mind ({quantumSourceLabel}) captured {formatPercentage(data[0].coveragePct)} observed
              risk coverage with {formatPercentage(data[0].testsPct)} of tests. The
              Random 50% and Full Suite rows are illustrative reference
              baselines.
            </>
          )}
        </Typography>
      </Alert>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Chart 4: Carbon impact                                            */
/* ------------------------------------------------------------------ */

/**
 * Render the carbon impact view for functional runs.
 *
 * @param props - Carbon impact props.
 * @returns Carbon impact tab.
 */
function CarbonImpactTab({
  comparatorData,
  runType,
  projectId,
}: {
  comparatorData: ComparatorBar[];
  runType: string;
  projectId?: string;
}) {
  const [cumulativeData, setCumulativeData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasComparisonData = Boolean(comparatorData && comparatorData.length >= 3);
  const quantumTime = hasComparisonData ? comparatorData[0].timeMinutes : 0;
  const fullSuiteTime = hasComparisonData ? comparatorData[2].timeMinutes : 0;
  const randomTime = hasComparisonData ? comparatorData[1].timeMinutes : 0;
  const timeSavedMinutes = fullSuiteTime - quantumTime;

  const impactPerRun = calculateCarbonImpact(timeSavedMinutes, 4, 'global');

  useEffect(() => {
    const controller = new AbortController();

    if (!hasComparisonData || !projectId) {
      setLoading(false);
      return () => controller.abort();
    }

    async function fetchCumulative() {
      try {
        setLoading(true);
        setError(null);
        const response = await getCarbonCumulative(projectId!, controller.signal);

        if (!response.ok) {
          throw new Error(`Carbon cumulative API failed: ${response.status}`);
        }

        const data = await response.json();
        setCumulativeData(data);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        console.error('Failed to fetch cumulative carbon data:', err);
        setError(
          'Carbon impact estimates could not be loaded. No carbon values are shown.'
        );
      } finally {
        setLoading(false);
      }
    }

    void fetchCumulative();

    return () => controller.abort();
  }, [hasComparisonData, projectId]);

  if (!hasComparisonData) {
    return (
      <Alert severity="info">
        Carbon impact estimates require what-if comparison data. No carbon or
        sustainability values are shown without that baseline.
      </Alert>
    );
  }

  if (!projectId) {
    return (
      <Alert severity="info">
        Carbon impact estimates require project activity data. No carbon or
        sustainability values are shown without that source.
      </Alert>
    );
  }

  if (timeSavedMinutes <= 0) {
    return (
      <Alert severity="info">
        Carbon impact estimates require test execution time baseline data. No
        carbon or sustainability values are shown without that source.
      </Alert>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (
    !loading &&
    (!cumulativeData ||
      typeof cumulativeData.total_runs !== 'number' ||
      cumulativeData.total_runs <= 0)
  ) {
    return (
      <Alert severity="info">
        Carbon impact estimates require cumulative project activity data. No
        carbon or sustainability values are shown yet.
      </Alert>
    );
  }

  const totalRuns =
    typeof cumulativeData?.total_runs === 'number' ? cumulativeData.total_runs : null;
  const daysActive =
    typeof cumulativeData?.days_active === 'number' ? cumulativeData.days_active : null;
  const observedRunsPerDay = calculateObservedRunsPerDay(totalRuns, daysActive);

  const co2PerRun = parseFloat(impactPerRun.raw.co2SavedKg);
  const cumulativeCO2 = co2PerRun * (totalRuns ?? 0);

  const co2PerMinute = co2PerRun / timeSavedMinutes;

  const quantumEmitted = quantumTime * (totalRuns ?? 0) * co2PerMinute;
  const randomEmitted = randomTime * (totalRuns ?? 0) * co2PerMinute;
  const fullSuiteEmitted = fullSuiteTime * (totalRuns ?? 0) * co2PerMinute;

  const quantumSaved = fullSuiteEmitted - quantumEmitted;
  const randomSaved = fullSuiteEmitted - randomEmitted;

  const comparisonData = [
    {
      strategy: 'QuantikMind',
      co2Emitted: quantumEmitted,
      co2Saved: quantumSaved,
      percentage: (quantumEmitted / fullSuiteEmitted) * 100,
      savingsPct: (
        ((fullSuiteEmitted - quantumEmitted) / fullSuiteEmitted) *
        100
      ).toFixed(0),
      color: COLORS.quantum,
      icon: '🏆',
      label: 'BEST',
    },
    {
      strategy: 'Random 50%',
      co2Emitted: randomEmitted,
      co2Saved: randomSaved,
      percentage: (randomEmitted / fullSuiteEmitted) * 100,
      savingsPct: (
        ((fullSuiteEmitted - randomEmitted) / fullSuiteEmitted) *
        100
      ).toFixed(0),
      color: '#6B7280',
      icon: '📊',
      label: 'GOOD',
    },
    {
      strategy: 'Full Suite',
      co2Emitted: fullSuiteEmitted,
      co2Saved: 0,
      percentage: 100,
      savingsPct: '0',
      color: '#374151',
      icon: '❌',
      label: 'BASELINE',
    },
  ];

  const maxEmitted = Math.max(...comparisonData.map((item) => item.co2Emitted));
  const yearlyProjectionKg = calculateAnnualCarbonProjectionKg(
    co2PerRun,
    observedRunsPerDay
  );
  const randomKgSavedPerRun =
    totalRuns && totalRuns > 0 ? Math.max(randomSaved / totalRuns, 0) : null;
  const randomYearlyProjectionKg = calculateAnnualCarbonProjectionKg(
    randomKgSavedPerRun,
    observedRunsPerDay
  );

  return (
    <Box>
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          background:
            'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
          borderRadius: 3,
          border: `2px solid ${COLORS.heatmap.low}`,
          mb: 4,
        }}
      >
        <Typography fontSize={14} fontWeight={600} color={COLORS.axis} mb={1}>
          TOTAL CARBON IMPACT ESTIMATE
        </Typography>

        {loading ? (
          <CircularProgress size={40} sx={{ color: COLORS.quantum }} />
        ) : (
          <>
            <Typography fontSize={13} color="white" mb={2}>
              {totalRuns} observed runs since{' '}
              {cumulativeData?.first_run
                ? new Date(cumulativeData.first_run).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'project start'}{' '}
              ({daysActive} days)
            </Typography>

            <Typography
              fontSize={56}
              fontWeight={900}
              color={COLORS.heatmap.low}
              sx={{
                textShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                fontFamily: 'monospace',
              }}
            >
              {cumulativeCO2.toFixed(2)} kg
            </Typography>

            <Typography fontSize={16} color="white" fontWeight={600} mt={1}>
              estimated CO₂e saved from observed runs = ~{kgToTonnes(cumulativeCO2).toFixed(4)} tonnes
            </Typography>

            <Box display="flex" justifyContent="center" gap={4} mt={3} flexWrap="wrap">
              <Box textAlign="center">
                <Typography fontSize={40}>🌳</Typography>
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color={COLORS.heatmap.low}
                >
                  {(
                    cumulativeCO2 / CARBON_CONSTANTS.TREE_ABSORPTION_KG_YEAR
                  ).toFixed(1)}
                </Typography>
                <Typography fontSize={11} color={COLORS.axis}>
                  tree-years equivalent
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography fontSize={40}>🚗</Typography>
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color={COLORS.coverage}
                >
                  {(
                    cumulativeCO2 / CARBON_CONSTANTS.CAR_EMISSION_KG_KM
                  ).toFixed(0)}{' '}
                  km
                </Typography>
                <Typography fontSize={11} color={COLORS.axis}>
                  km avoided equivalent
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography fontSize={40}>⚡</Typography>
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color={COLORS.quantum}
                >
                  {(
                    (cumulativeCO2 / CARBON_CONSTANTS.INTENSITY_GLOBAL) *
                    1000
                  ).toFixed(0)}{' '}
                  kWh
                </Typography>
                <Typography fontSize={11} color={COLORS.axis}>
                  kWh equivalent
                </Typography>
              </Box>

              <Box textAlign="center">
                <Typography fontSize={40}>🏠</Typography>
                <Typography fontSize={20} fontWeight={700} color="#8B5CF6">
                  {(
                    (cumulativeCO2 / CARBON_CONSTANTS.HOME_HEATING_MONTH_KG) *
                    30
                  ).toFixed(1)}
                </Typography>
                <Typography fontSize={11} color={COLORS.axis}>
                  heating-days equivalent
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          p: 3,
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: 3,
          border: '2px solid rgba(255, 255, 255, 0.1)',
          mb: 4,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <Typography variant="h6" fontWeight={800} color="white">
            💨 Estimated CO₂e Comparison
          </Typography>
          <Tooltip title="Illustrative estimate based on execution-time baselines and carbon assumptions. Not measured emissions.">
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color: COLORS.axis, cursor: 'help' }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {comparisonData.map((item, index) => {
            const widthPercent = (item.co2Emitted / maxEmitted) * 100;

            const comparison =
              index === 0
                ? `${formatPercentage(Number(item.savingsPct))} less than full suite`
                : index === 1
                ? `${formatPercentage(
                    (item.co2Emitted / comparisonData[0].co2Emitted - 1) *
                    100
                  )} more than QuantikMind`
                : 'baseline';

            return (
              <Box key={index} mb={2.5}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.5}
                >
                  <Typography fontSize={13} fontWeight={700} color="white">
                    {item.icon} {item.strategy}{' '}
                    {index === 0 && (
                      <span style={{ color: COLORS.quantum }}>← YOU</span>
                    )}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize={14} fontWeight={700} color={item.color}>
                      {item.co2Emitted.toFixed(2)} kg emitted
                    </Typography>
                    <Typography
                      fontSize={11}
                      fontWeight={600}
                      color={index === 0 ? COLORS.heatmap.low : COLORS.heatmap.high}
                    >
                      ({formatPercentage(Number(item.savingsPct))} saved)
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: 44,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${widthPercent}%`,
                      height: '100%',
                      bgcolor: item.color,
                      transition: 'width 1s ease-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 2,
                    }}
                  >
                    <Typography fontSize={12} fontWeight={700} color="white">
                      {item.label}
                    </Typography>
                  </Box>
                </Box>

                <Typography fontSize={10} color={COLORS.axis} mt={0.5}>
                  {comparison}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Alert
          severity="success"
          sx={{
            mt: 3,
            bgcolor: 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${COLORS.heatmap.low}`,
          }}
        >
          <Typography fontWeight={700} fontSize={14} color={COLORS.heatmap.low}>
            Illustrative estimate: {comparisonData[0].co2Saved.toFixed(2)} kg
            CO₂e saved versus the full-suite reference across observed runs
          </Typography>
          <Typography fontSize={12} color={COLORS.axis}>
            These values are modeled from estimated execution time, cumulative
            run count, and published carbon assumptions. They are not measured
            emissions or ESG results.
          </Typography>
        </Alert>
      </Box>

      <Box
        sx={{
          p: 3,
          bgcolor: 'rgba(57, 255, 20, 0.05)',
          border: `2px solid ${COLORS.quantum}`,
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Typography variant="h6" fontWeight={800} color="white" mb={2}>
          Yearly Projection Estimate
        </Typography>

        <Typography fontSize={14} color={COLORS.axis} mb={3}>
          Based on observed project activity when available. Formula: estimated
          kg CO₂e saved per run × observed runs/day × 365.
        </Typography>

        {observedRunsPerDay == null || yearlyProjectionKg == null ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Yearly projection unavailable because observed run frequency is not
            available from project activity data.
          </Alert>
        ) : (
          <Typography fontSize={13} color={COLORS.axis} mb={3}>
            Observed run frequency: ~{observedRunsPerDay.toFixed(1)} runs/day.
          </Typography>
        )}

        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
          gap={3}
          mb={3}
        >
          <Box textAlign="center">
            <Typography fontSize={11} color={COLORS.axis} mb={1}>
              QuantikMind
            </Typography>
            <Typography fontSize={24} fontWeight={700} color={COLORS.quantum}>
              {yearlyProjectionKg == null
                ? 'Unavailable'
                : `~${kgToTonnes(yearlyProjectionKg).toFixed(3)}t/yr`}
            </Typography>
            {yearlyProjectionKg != null && (
              <Typography fontSize={20}>
                {(
                  yearlyProjectionKg / CARBON_CONSTANTS.TREE_ABSORPTION_KG_YEAR
                ).toFixed(0)}{' '}
                🌳
              </Typography>
            )}
            <Typography fontSize={10} color={COLORS.quantum} mt={0.5}>
              estimated saved
            </Typography>
          </Box>

          <Box textAlign="center">
            <Typography fontSize={11} color={COLORS.axis} mb={1}>
              Random 50%
            </Typography>
            <Typography fontSize={24} fontWeight={700} color="#6B7280">
              {randomYearlyProjectionKg == null
                ? 'Unavailable'
                : `~${kgToTonnes(randomYearlyProjectionKg).toFixed(3)}t/yr`}
            </Typography>
            {randomYearlyProjectionKg != null && (
              <Typography fontSize={20}>
                {(
                  randomYearlyProjectionKg / CARBON_CONSTANTS.TREE_ABSORPTION_KG_YEAR
                ).toFixed(0)}{' '}
                🌳
              </Typography>
            )}
            <Typography fontSize={10} color="#6B7280" mt={0.5}>
              illustrative saved
            </Typography>
          </Box>

          <Box textAlign="center">
            <Typography fontSize={11} color={COLORS.axis} mb={1}>
              Full Suite
            </Typography>
            <Typography fontSize={24} fontWeight={700} color="#374151">
              baseline
            </Typography>
            <Typography fontSize={10} color="#374151" mt={0.5}>
              reference
            </Typography>
          </Box>
        </Box>

        {yearlyProjectionKg != null && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'rgba(16, 185, 129, 0.1)',
              borderRadius: 2,
              border: '1px solid rgba(16, 185, 129, 0.3)',
            }}
          >
            <Typography fontSize={13} fontWeight={700} color={COLORS.heatmap.low}>
              Annual estimate: ~{kgToTonnes(yearlyProjectionKg).toFixed(3)} tonnes CO₂e saved
            </Typography>
            <Typography fontSize={12} color={COLORS.axis} mt={0.5}>
              = {(
                yearlyProjectionKg / CARBON_CONSTANTS.TREE_ABSORPTION_KG_YEAR
              ).toFixed(0)}{' '}
              tree-years equivalent
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          mb: 3,
        }}
      >
        <Typography fontSize={11} fontWeight={700} color={COLORS.axis} mb={1}>
          📌 Per Run Average
        </Typography>
        <Typography fontSize={12} color={COLORS.axis}>
          Each test run with QuantikMind is estimated to save approximately{' '}
          <strong style={{ color: COLORS.quantum }}>
            ~{impactPerRun.raw.co2SavedKg} kg CO₂e
          </strong>{' '}
          (~{impactPerRun.raw.co2SavedTonnes} tonnes CO₂e, ~
          {impactPerRun.raw.energySavedKWh} kWh, ~
          {impactPerRun.raw.cpuHoursSaved} CPU hours)
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography fontSize={12} fontWeight={700} color="white" mb={1}>
          📐 Calculation Methodology
        </Typography>
        <Typography fontSize={11} color={COLORS.axis} lineHeight={1.6}>
          Illustrative estimate based on average cloud computing energy consumption across Google Cloud
          (PUE 1.10), AWS (PUE 1.20), and Azure (PUE 1.125). Carbon intensity
          uses global grid average of 475 gCO₂e/kWh (2024). Assumes 4 vCPUs per
          test execution at 12.5W per CPU hour. Tree absorption rate: 21kg
          CO₂e/year per mature tree. Car emissions: 120g CO₂e/km. Annual
          projection uses per-run saving × observed runs/day × 365.
        </Typography>
        <Typography fontSize={11} color={COLORS.axis} mt={1}>
          <strong>Sources:</strong> EPA, Carbon Trust, Google Environmental
          Report 2024, AWS Sustainability Report, Azure Carbon Calculator
        </Typography>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/**
 * Render the Quantum analytics container for a run.
 *
 * @param props - Component props.
 * @returns Quantum analytics component.
 */
export default function QuantumGraph({
  run,
  projectId,
}: {
  run: Run;
  projectId?: string;
}) {
  const [tab, setTab] = useState<'trend' | 'heatmap' | 'comparator' | 'carbon'>(
    'trend'
  );
  const { data, loading, error } = useRunAnalytics(run, projectId);

  const runType = (run as any)?.run_type || 'chaos';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress sx={{ color: COLORS.quantum }} />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">{error || 'Failed to load analytics data'}</Alert>;
  }

  return (
    <Box mt={2}>
      <Typography variant="h6" fontWeight={900} gutterBottom>
        Quantum Analytics
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            fontWeight: 700,
            fontSize: 13,
            color: COLORS.axis,
            '&.Mui-selected': {
              color: COLORS.quantum,
            },
          },
        }}
      >
        <Tab value="trend" label="HISTORICAL TREND (30 days)" />
        <Tab value="heatmap" label="SERVICE RISK HEATMAP" />
        <Tab value="comparator" label="WHAT-IF COMPARATOR" />
        {runType === 'functional' && (
          <Tab value="carbon" label="CARBON IMPACT" />
        )}
      </Tabs>

      <Box
        sx={{
          border: `1px solid ${COLORS.grid}`,
          borderRadius: 3,
          p: 3,
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          minHeight: 500,
        }}
      >
        {tab === 'trend' && (
          <EfficiencyTrendChart data={data.trendData} currentRun={run} />
        )}

        {tab === 'heatmap' && (
          <ServiceRiskHeatmap
            runs={data.heatmapRuns}
            data={data.heatmapData}
            currentRunId={
              run.run_id ??
              data.heatmapRuns?.[data.heatmapRuns.length - 1]?.run_id
            }
          />
        )}

        {tab === 'comparator' && (
          <WhatIfComparator
            data={data.comparatorData}
            efficiencyMultiplier={data.efficiencyMultiplier}
            runType={runType}
            error={data.comparatorError}
          />
        )}

        {tab === 'carbon' && (
          <CarbonImpactTab
            comparatorData={data.comparatorData}
            runType={runType}
            projectId={projectId}
          />
        )}
      </Box>
    </Box>
  );
}
